import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/http";
import {
  fetchStaffNotifications,
  markNotificationRead,
  NOTIFICATION_CATEGORIES,
  type StaffNotification,
} from "../../../api/staffApi";
import { categoryLabel } from "../staffFormat";
import { EmptyState } from "../components/EmptyState";
import { NotificationRow } from "../components/NotificationRow";
import { SkeletonCard } from "../components/SkeletonCard";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";

export function StaffNotificationsPage() {
  const [category, setCategory] = useState<string>("");
  const [items, setItems] = useState<StaffNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchStaffNotifications(category || undefined));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not mark read.");
    }
  };

  return (
    <StaffAppShell title="Notifications" showBack backTo="/staff/me" hideNav>
      <div className="staff-page">
        <label className="staff-field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {NOTIFICATION_CATEGORIES.map((c) => (
              <option key={c} value={c}>{categoryLabel(c)}</option>
            ))}
          </select>
        </label>

        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}

        {loading ? (
          <SkeletonCard />
        ) : items.length === 0 ? (
          <EmptyState title="No notifications" detail="You're all caught up." />
        ) : (
          <div className="staff-notification-list">
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} onRead={onRead} />
            ))}
          </div>
        )}
      </div>
    </StaffAppShell>
  );
}
